/**
 * useQuizExport Hook
 * 
 * Provides export functionality for quiz questions and results.
 * Downloads data as JSON files.
 */

import { useCallback } from 'react';
import { toExportFormatArray } from '../utils/questionSchemaConverter';
import { notifyWarning } from '../services/notificationService';

/**
 * Download helper - creates temporary link and triggers download
 */
const downloadJSON = (data, fileName) => {
  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(data, null, 2));
  
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute("href", dataStr);
  downloadAnchorNode.setAttribute("download", fileName);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

/**
 * Hook for quiz export functionality
 * 
 * @param {Object} params
 * @param {Array} params.questions - Quiz questions array
 * @param {Object} params.allAnswers - All answers { questionIndex: { odId: answerData } }
 * @param {Object} params.participants - Participants object { odId: participantData }
 * @param {string} params.sessionCode - Session code for filename
 * @param {string} params.instanceId - Instance ID for filename fallback
 * @returns {{ exportQuestions: Function, exportResults: Function, canExportQuestions: boolean, canExportResults: boolean }}
 */
export function useQuizExport({ questions = [], allAnswers = {}, participants = {}, sessionCode, instanceId }) {
  
  /**
   * Check if questions can be exported
   */
  const canExportQuestions = questions.length > 0;
  
  /**
   * Check if results can be exported
   */
  const canExportResults = questions.length > 0 && Object.keys(allAnswers).length > 0;

  /**
   * Export questions to JSON
   */
  const exportQuestions = useCallback(() => {
    if (!canExportQuestions) {
      notifyWarning('Keine Fragen zum Exportieren vorhanden.');
      return;
    }
    
    const exportData = toExportFormatArray(questions);
    const fileName = `quiz-fragen-${sessionCode || instanceId}.json`;
    downloadJSON(exportData, fileName);
  }, [questions, sessionCode, instanceId, canExportQuestions]);

  /**
   * Export results to JSON
   */
  const exportResults = useCallback(() => {
    if (!canExportResults) {
      notifyWarning('Keine Ergebnisse zum Exportieren vorhanden.');
      return;
    }

    const resultsData = questions.map((question, questionIndex) => {
      const questionAnswers = allAnswers[questionIndex] || {};
      const totalResponses = Object.keys(questionAnswers).length;
      
      // Count answers per option
      const optionCounts = {};
      question.options?.forEach((_, index) => {
        optionCounts[index] = 0;
      });
      
      Object.values(questionAnswers).forEach(answer => {
        const answerIndex = typeof answer === 'object' ? answer.selectedIndex : answer;
        if (answerIndex !== null && answerIndex !== undefined) {
          optionCounts[answerIndex] = (optionCounts[answerIndex] || 0) + 1;
        }
      });
      
      // Get correct answer
      const correctIndex = question.correctAnswerIndex ?? question.correctAnswer ?? 0;
      const correctCount = optionCounts[correctIndex] || 0;
      const correctPercentage = totalResponses ? ((correctCount / totalResponses) * 100).toFixed(1) : 0;
      
      return {
        questionNumber: questionIndex + 1,
        questionText: question.question || question.text,
        totalResponses,
        correctAnswerIndex: correctIndex,
        correctAnswerText: question.options?.[correctIndex] || '',
        correctCount,
        correctPercentage: `${correctPercentage}%`,
        options: question.options?.map((option, index) => ({
          text: option,
          count: optionCounts[index] || 0,
          percentage: totalResponses ? `${((optionCounts[index] / totalResponses) * 100).toFixed(1)}%` : '0%',
          isCorrect: index === correctIndex
        })) || [],
        participantResponses: Object.entries(questionAnswers).map(([participantId, answer]) => {
          const answerIndex = typeof answer === 'object' ? answer.selectedIndex : answer;
          return {
            participantId,
            participantName: participants[participantId]?.name || participantId,
            selectedOption: answerIndex,
            selectedText: question.options?.[answerIndex] || '',
            isCorrect: answerIndex === correctIndex,
            responseTimeMs: typeof answer === 'object' ? answer.responseTimeMs : null
          };
        })
      };
    });
    
    const fileName = `quiz-ergebnisse-${sessionCode || instanceId}.json`;
    downloadJSON(resultsData, fileName);
  }, [questions, allAnswers, participants, sessionCode, instanceId, canExportResults]);

  return {
    exportQuestions,
    exportResults,
    canExportQuestions,
    canExportResults
  };
}

export default useQuizExport;
